import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { effect } from '@preact/signals-core'
import { useBootstrapModal } from '../../modules/drive/bootstrap-modal.js'
import { useDriveStore, useDriveI18n } from './drive-context.js'

export function UploadModal({ onCancel }) {
  const store = useDriveStore()
  const i18n = useDriveI18n()
  const t = (key) => i18n.t(key)

  const [open, setOpen] = useState(store.uploadOpen.value)
  const [files, setFiles] = useState(store.uploadFiles.value)
  const [overall, setOverall] = useState(store.uploadOverallPercent.value)
  const [statusText, setStatusText] = useState(store.uploadStatusText.value)
  const [serverProcessing, setServerProcessing] = useState(store.uploadServerProcessing.value)

  useEffect(() => effect(() => {
    setOpen(store.uploadOpen.value)
    setFiles(store.uploadFiles.value)
    setOverall(store.uploadOverallPercent.value)
    setStatusText(store.uploadStatusText.value)
    setServerProcessing(store.uploadServerProcessing.value)
  }), [store])

  const handleHidden = useCallback(() => {
    store.setUploadState({ open: false })
  }, [store])

  const modalRef = useBootstrapModal(open, handleHidden)

  return (
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref={modalRef}>
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" style="width: min(720px, 100%);">
        <div class="modal-content">
          <div class="modal-header border-bottom">
            <h5 class="modal-title"><i class="bi bi-cloud-arrow-up me-2" />{t('drive.uploading_files')}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div class="modal-body">
            <div class="mb-4 upload-modal-overall-progress">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">{t('drive.overall_progress')}</h6>
                <small class="text-muted">{Math.round(overall)}%</small>
              </div>
              <div class="progress" style="height: 8px;">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  style={`width: ${overall}%`}
                  aria-valuenow={Math.round(overall)}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <small class="text-muted d-block mt-2">{statusText || t('drive.preparing_upload')}</small>
            </div>

            {serverProcessing && (
              <div class="mb-3 p-3 bg-light rounded border">
                <div class="d-flex align-items-center gap-2">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">{t('drive.processing')}</span>
                  </div>
                  <span class="text-muted small">{t('drive.server_processing')}</span>
                </div>
              </div>
            )}

            <div class="files-list upload-modal-files-list">
              {files.map((f) => (
                <div key={f.id} class="d-flex align-items-center gap-2 mb-2">
                  <div class="flex-grow-1 text-truncate small">{f.name}</div>
                  <div class="progress flex-grow-1" style="height: 6px; max-width: 40%;">
                    <div
                      class={`progress-bar ${f.status === 'error' ? 'bg-danger' : f.status === 'done' ? 'bg-success' : 'bg-primary'}`}
                      style={`width: ${f.progress || 0}%`}
                    />
                  </div>
                  <small class="text-muted" style="width: 3rem; text-align: right;">{Math.round(f.progress || 0)}%</small>
                </div>
              ))}
            </div>
          </div>
          <div class="modal-footer border-top">
            <button type="button" class="btn btn-outline-danger" onClick={() => onCancel?.()}>
              {t('drive.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
